import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
// import { addLead, updateLead } from "../../redux/leadsSlice"; // Import actions
import {
  addLead,
  setChildLeads,
  setMode,
  setParentLead,
  setSelectedLead,
  setStats,
  updateLead,
} from "../../../state/reducers/leadSlice";
import CreateLeadForm from "./CreateLeadForm";
import { LeadCards } from "../../../services/admin.services";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { toggleLeadFormOpen } from "../../../state/reducers/applicationSlice";
import LeadManagementTabs from "./LeadsManagementTabs";
import Modal from "react-modal";

function LeadsHeader({ fetchLeads }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const existingLeads = useSelector((state) => state.leads.leads); // Get current leads from state
  const selectedLead = useSelector((state) => state.leads.selectedLead);
  const isLeadFormOpen = useSelector(
    (state) => state.application.isLeadFormOpen
  );
  // const [isModalOpen, setIsModalOpen] = useState(false);
  // const [metrics, setMetrics] = useState({
  //   enquiries: 0,
  //   enquiryLeads: 0,
  //   customerLeads: 0,
  // });
  const metrics = useSelector((state) => state.leads.stats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null); // Store filename
  const [parsedLeads, setParsedLeads] = useState([]); // Store parsed leads from Excel

  const toggleModal = () => {
    // setIsModalOpen(!isModalOpen);
    // document.body.style.overflow = isModalOpen ? "auto" : "hidden";
    dispatch(setParentLead(""));
    dispatch(setSelectedLead(""));
    dispatch(setChildLeads([]));
    dispatch(toggleLeadFormOpen());
  };

  const handleCreateLead = () => {
    dispatch(setMode("new"));
    dispatch(setParentLead(""));
    dispatch(setSelectedLead(""));
    dispatch(setChildLeads([]));
    dispatch(toggleLeadFormOpen());
  };

  // Fetch metrics on component mount
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const response = await LeadCards(); // Fetch data from API
        const { summary } = response.data.results;

        dispatch(
          setStats({
            enquiries: summary.total_lead || 0,
            enquiryLeads: summary.fresh_to_enquiries || 0,
            customerLeads: summary.enquiries_to_customer || 0,
          })
        );
      } catch (err) {
        console.error("Error fetching metrics:", err);
        // setError(err.message || "Failed to load metrics.");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFileName(file.name); // Save file name
      const reader = new FileReader();
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Normalize keys to match Redux fields
        const normalizedData = normalizeKeys(jsonData);

        setParsedLeads(normalizedData); // Save normalized leads for further processing
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Normalize keys to match Redux fields
  const normalizeKeys = (data) => {
    return data.map((row) => {
      const normalizedRow = Object.keys(row).reduce((acc, key) => {
        const normalizedKey = key.toLowerCase().replace(/\s+/g, "_"); // Convert to lowercase and replace spaces with underscores
        acc[normalizedKey] = row[key];
        return acc;
      }, {});

      // Add default values for missing fields
      normalizedRow.created_at = normalizedRow.created_at || "N/A";
      normalizedRow.branch = normalizedRow.branch || "Unknown";

      return normalizedRow;
    });
  };

  // Handle file delete
  const handleFileDelete = () => {
    setUploadedFileName(null); // Clear file name
    setParsedLeads([]); // Clear parsed data
  };

  // Inside handleFileSubmit:

  // Handle submit
  const handleFileSubmit = () => {
    if (parsedLeads.length > 0) {
      // Validation function to ensure all required fields are present
      const validateLead = (lead) => {
        const requiredFields = [
          "name",
          "status",
          "lead_type",
          "created_at",
          "branch",
        ];
        return requiredFields.every((field) => lead[field]);
      };

      // Assign unique IDs to each lead and process
      const leadsWithIds = parsedLeads.map((newLead, index) => ({
        ...newLead,
        id: Date.now() + index, // Generate a unique ID using timestamp + index
      }));

      leadsWithIds.forEach((newLead) => {
        if (validateLead(newLead)) {
          // Check if the lead already exists in Redux state
          const existingLead = existingLeads.find(
            (lead) => lead.name.toLowerCase() === newLead.name.toLowerCase()
          );

          if (existingLead) {
            // Update the existing lead
            dispatch(updateLead({ ...existingLead, ...newLead }));
          } else {
            // Add a new lead
            dispatch(addLead(newLead));
          }
        } else {
          console.error("Invalid lead data:", newLead);
        }
      });

      // Notify the user of successful processing
      alert("Leads processed successfully!");

      // Clear the uploaded file and parsed leads data
      setUploadedFileName(null);
      setParsedLeads([]);
    } else {
      alert("No leads to process.");
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 w-full overflow-x-hidden">
      {/* Main Content */}
      <div className="bg-white border-2 border-purple-500 rounded-lg shadow-md p-4 sm:p-6 w-full max-w-full">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 gap-3">
          <h2 className="text-xl font-bold text-gray-700">Leads</h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={handleCreateLead}
              className="px-3 sm:px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 text-sm sm:text-base whitespace-nowrap"
            >
              + New Lead
            </button>
            <label
              className="relative"
              onClick={() => navigate("/admin/import-leads")}
            >
              <span className="px-3 sm:px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 cursor-pointer text-sm sm:text-base whitespace-nowrap">
                Import
              </span>
            </label>
          </div>
        </div>

        {/* Content Section */}
        <div className="mt-4 sm:mt-6 overflow-x-auto">
          {loading ? (
            <p className="text-center py-4">Loading metrics...</p>
          ) : error ? (
            <p className="text-red-500 text-center py-4">{error}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 w-full">
              {/* Total Enquiries */}
              <div className="bg-green-400 p-3 sm:p-4 rounded-md shadow-md flex flex-col h-full min-w-[180px]">
                <div className="flex-1">
                  <p className="text-base sm:text-lg font-bold text-white">
                    Total Leads
                  </p>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mt-1">
                    {metrics.enquiries}
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-white mt-2">
                  Total number of leads in the last 30 days.
                </p>
              </div>

              {/* Enquiries → Leads */}
              <div className="bg-blue-400 p-3 sm:p-4 rounded-md shadow-md flex flex-col h-full min-w-[180px]">
                <div className="flex-1">
                  <p className="text-base sm:text-lg font-bold text-white">
                    Leads → Enquiries
                  </p>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mt-1">
                    {metrics.enquiryLeads}
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-white mt-2">
                  Converted enquiries from leads.
                </p>
              </div>

              {/* Enquiries → Customer */}
              <div className="bg-yellow-500 p-3 sm:p-4 rounded-md shadow-md flex flex-col h-full min-w-[180px]">
                <div className="flex-1">
                  <p className="text-base sm:text-lg font-bold text-white">
                    Enquiries → Customer
                  </p>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mt-1">
                    {metrics.customerLeads}
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-white mt-2">
                  Converted customer from enquiries.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isLeadFormOpen && (
        <div className="fixed top-0 inset-0 bg-black bg-opacity-50 flex items-start justify-center z-[1000] px-4">
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl relative"
            style={{ maxHeight: "calc(100vh - 110px)" }}
          >
            <LeadManagementTabs
              toggleModal={toggleModal}
              fetchLeads={fetchLeads}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsHeader;
